import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { TextField } from '@radix-ui/themes'
import React from 'react'
import styled from 'styled-components'

const StyledTextFieldRoot = styled(TextField.Root)`
  min-height: 2rem;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverButton} !important;
  }

  &:focus-within {
    border: 1px solid ${({ theme }) => theme.forecast.activeText};
  }

  input::selection {
    background: ${({ theme }) => theme.forecast.primary}66;
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const StyledTextFieldSlot = styled(TextField.Slot)`
  padding: 4px;
`

function formatResultCountMessage(
  query: string,
  resultCount?: number
): string | undefined {
  if (!query.trim() || resultCount === undefined) {
    return undefined
  }

  return plural(resultCount, {
    0: 'No results',
    one: '# result',
    other: '# results',
  })
}

interface SearchBarProps {
  value: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  style?: React.CSSProperties
  className?: string
  onKeyDown?: (e: React.KeyboardEvent) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  disabled?: boolean
  resultCount?: number
  ariaLabel?: string
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder,
  style,
  className,
  onKeyDown,
  onFocus,
  disabled,
  resultCount,
  ariaLabel,
}) => {
  const { t } = useLingui()
  const statusMessage = formatResultCountMessage(value, resultCount)
  // Prefer a descriptive label (e.g. "Search Files") when provided, appending
  // the live result count if present. Fall back to the count-only message to
  // preserve prior behavior for callers that don't pass a label.
  const accessibleLabel = ariaLabel
    ? statusMessage
      ? `${ariaLabel}, ${statusMessage}`
      : ariaLabel
    : statusMessage

  return (
    <StyledTextFieldRoot
      placeholder={placeholder ?? t`Search`}
      variant='soft'
      value={value}
      className={className}
      style={style}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      disabled={disabled}
      aria-label={accessibleLabel}>
      <StyledTextFieldSlot>
        <MagnifyingGlassIcon height='16' width='16' />
      </StyledTextFieldSlot>
    </StyledTextFieldRoot>
  )
}

export default SearchBar
